import _ from 'lodash'
import Vue from 'vue'
import i18n from '_/i18n'
import { mapActions, mapGetters } from 'vuex'

const InputTextField = Vue.component(
  'InputTextField',
  require('_/components/FieldTypes/InputTag/InputTag.vue').default
)

const InvitationsButtons = Vue.component(
  'InvitationsButtons',
  require('_/components/UserInterface/EntryForm/InvitationsButtons/InvitationsButtons.vue')
    .default
)

const ImpersonateButton = Vue.component(
  'InvitationsButtons',
  require('_/components/UserInterface/EntryForm/ImpersonateButton/ImpersonateButton.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    admin: {
      required: true,
      type: Object,
    },
    disableDelete: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * Set the data property
   *
   * @return  {object}
   */
  data() {
    return {}
  },

  /**
   * Set the computed properties
   *
   * @type {object}
   */
  computed: {
    ...mapGetters({
      photonModule: 'photonModule/photonModule',
    }),

    mainButton() {
      if (this.admin.editorMode === 'create') {
        return {
          shouldRender: true,
          disabled:
            this.admin.submitInProgress ||
            this.dataOverdraft ||
            this.checkPermission('create'),
          action: this.submit || null,
          text: this.newEntryButtonText,
        }
      }

      if (this.admin.editorMode === 'edit') {
        return {
          shouldRender: true,
          disabled:
            this.admin.submitInProgress || !this.checkPermission('update'),
          action: this.submit,
          text: i18n.t('admin.saveChanges'),
        }
      }

      return {
        shouldRender: false,
      }
    },

    /**
     * Returns an array of dropdown menu options
     *
     * @returns Array
     */
    dropdownOptions() {
      return [
        // Publish option in edit mode
        {
          shouldRender: this.admin.editorMode === 'edit',
          disabled:
            this.admin.submitInProgress || !this.checkPermission('update'),
          action:
            this.isAlreadyPublished || this.isScheduledForPublishing
              ? this.unpublish
              : this.publish,
          text: this.publishText,
        },
        // Save and publish option
        {
          shouldRender:
            this.admin.editorMode === 'create' &&
            !(this.admin.selectedModule.options?.is_system ?? this.admin.selectedModule.is_system ?? 0),
          disabled:
            this.admin.submitInProgress || !this.checkPermission('create'),
          action: this.publish,
          text: i18n.t('admin.saveAndPublish'),
        },
        // Schedule publishing option
        {
          shouldRender:
            !(this.admin.selectedModule.options?.is_system ?? this.admin.selectedModule.is_system ?? 0) &&
            this.admin.selectedModule.table_name !== 'assets' &&
            !(this.isAlreadyPublished || this.isScheduledForPublishing),
          disabled:
            this.admin.submitInProgress || !this.checkPermission('create'),
          action: this.schedulePublishing,
          text: i18n.t('admin.saveAndSchedulePublishing'),
        },
        // Duplicate option
        {
          shouldRender:
            this.admin.editorMode === 'edit' &&
            this.admin.selectedModule.type != 'single_entry',
          disabled:
            this.admin.submitInProgress || !this.checkPermission('update'),
          action: this.duplicate,
          text: i18n.t('admin.duplicate'),
        },
        // Delete entry option
        {
          shouldRender:
            this.admin.editorMode === 'edit' &&
            this.admin.selectedModule.type != 'single_entry' &&
            !this.disableDelete,
          disabled:
            this.admin.submitInProgress || !this.checkPermission('delete'),
          action: this.deleteEntry,
          text: i18n.t('admin.deleteEntry'),
        },
      ]
    },

    /**
     * Checks if entry is already published
     *
     * @returns boolean
     */
    isAlreadyPublished() {
      const dateTime = moment().format('YYYY-MM-DD HH:mm:ss')

      return moment(dateTime).isSameOrAfter(
        _.get(this.admin.editedEntry, 'system.published_at')
      )
    },

    /**
     * Checks if entry is scheduled for publishing
     *
     * @returns boolean
     */
    isScheduledForPublishing() {
      if (!_.get(this.admin.editedEntry, 'system.published_at')) {
        return false
      }

      const dateTime = moment().format('YYYY-MM-DD HH:mm:ss')
      return moment(dateTime).isBefore(
        _.get(this.admin.editedEntry, 'system.published_at')
      )
    },

    /**
     * Returns an unpublished option text
     *
     * @returns string
     */
    publishText() {
      if (this.admin.dirtyFields.length > 0) {
        if (this.isAlreadyPublished) {
          return i18n.t('admin.saveAndUnpublish')
        }

        if (this.isScheduledForPublishing) {
          return i18n.t('admin.saveAndUnschedule')
        }

        return i18n.t('admin.saveAndPublish')
      }

      if (this.isAlreadyPublished) {
        return i18n.t('admin.unpublish')
      }

      if (this.isScheduledForPublishing) {
        return i18n.t('admin.unschedule')
      }

      return i18n.t('admin.publish')
    },

    /**
     * Returns an action button text
     *
     * @returns string
     */
    newEntryButtonText() {
      if (this.admin.selectedModule.table_name === 'invitations') {
        return i18n.t('admin.sendInvitation')
      }

      if (this.admin.selectedModule.options?.is_system ?? this.admin.selectedModule.is_system ?? 0) {
        return i18n.t('admin.create')
      }

      return i18n.t('admin.saveAsDraft')
    },

    /**
     * Checks the overdraft status
     *
     * @returns boolean
     */
    dataOverdraft() {
      let selectedModuleTableName = this.admin.selectedModule.table_name
      let selectedModuleId = this.photonModule.moduleTableNameToIdMap[
        selectedModuleTableName
      ]
      let isOverdraft = this.photonModule.moduleIdToModuleObjectMap[
        selectedModuleId
      ].overdraft_status
      return isOverdraft
    },
  },

  /**
   * Set the methods
   *
   * @type  {Object}
   */
  methods: {
    /**
     * Checks if the given permission type is approved or not
     *
     * @param   {string}  permissionType
     * @return  boolean
     */
    checkPermission(permissionType) {
      if (
        !_.has(
          this.admin,
          `editedEntry.system.permission_control.crud.${permissionType}`
        )
      ) {
        return false
      }

      return !!_.get(
        this.admin,
        `editedEntry.system.permission_control.crud.${permissionType}`
      )
    },

    publish() {
      this.$emit('publish')
    },

    schedulePublishing() {
      this.$emit('schedulePublishing')
    },

    submit() {
      this.$emit('submit')
    },

    unpublish() {
      this.$emit('unpublish')
    },

    duplicate() {
      this.$emit('duplicate')
    },

    deleteEntry() {
      this.$emit('deleteEntry')
    },
  },

  /**
   * Set the mounted hook
   *
   * @return  {void}
   */
  mounted() {},

  /**
   * Set the watched properties
   *
   * @type  {Object}
   */
  watch: {},
}
